import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import PixelAgent from './PixelAgent';

const AGENTS = [
  {
    id: 'auto',
    name: 'Super Agent',
    short: 'Super',
    desc: 'Idea to app, fully autonomous',
    charIndex: 0,
  },
  {
    id: 'ideation',
    name: 'Ideation Agent',
    short: 'Ideation',
    desc: 'Sharpens your idea into a clear concept',
    charIndex: 1,
  },
  {
    id: 'research',
    name: 'Research Agent',
    short: 'Research',
    desc: 'Deep market & competitor analysis',
    charIndex: 2,
  },
  {
    id: 'prd',
    name: 'PRD Agent',
    short: 'PRD',
    desc: 'Writes a full product requirements doc',
    charIndex: 3,
  },
  {
    id: 'design',
    name: 'Design Agent',
    short: 'Design',
    desc: 'Crafts the UI/UX and visual system',
    charIndex: 4,
  },
  {
    id: 'code',
    name: 'Code Agent',
    short: 'Code',
    desc: 'Builds and deploys the working app',
    charIndex: 5,
  },
];

export { AGENTS };

const AgentPicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const ref = useRef(null);
  const agent = value || 'auto';
  const current = AGENTS.find((a) => a.id === agent) || AGENTS[0];
  const preview = AGENTS.find((a) => a.id === hoveredId) || current;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="agent-picker-button"
        className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] border border-transparent hover:border-[#E2E8F0] transition-colors"
      >
        <PixelAgent charIndex={current.charIndex} mode="type" scale={1} />
        <span className="font-semibold">{current.name}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute bottom-full mb-2 left-0 bg-white border border-[#E2E8F0] rounded-2xl z-30 overflow-hidden"
          style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.12)', width: 220 }}
          data-testid="agent-picker-dropdown"
        >
          <div className="py-1">
            {AGENTS.map((a) => {
              const isSelected = agent === a.id;
              const isHovered = hoveredId === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => { onChange(a.id); setOpen(false); }}
                  onMouseEnter={() => setHoveredId(a.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  data-testid={`agent-pick-${a.id}`}
                  className={`w-full flex items-center gap-2 px-2.5 py-1 transition-colors ${
                    isSelected ? 'bg-[#EFF6FF]' : isHovered ? 'bg-[#F8FAFC]' : ''
                  }`}
                >
                  <div className="flex-shrink-0 flex items-center justify-center w-5">
                    <PixelAgent
                      charIndex={a.charIndex}
                      mode={isSelected || isHovered ? 'type' : 'walk'}
                      scale={1}
                    />
                  </div>
                  <div className="flex-1 text-left">
                    <div className={`text-[11px] font-semibold leading-tight ${isSelected ? 'text-[#2563EB]' : 'text-[#0F172A]'}`}>
                      {a.name}
                    </div>
                    <div className="text-[9px] text-[#94A3B8] leading-tight">{a.desc}</div>
                  </div>
                  {isSelected && (
                    <div className="w-1.5 h-1.5 rounded-full bg-[#2563EB] flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentPicker;
